import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetUserQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {interestRoutes} from '@dms/routes';
import {EMPTY_PLACEHOLDER, getTimeToFirstResponse, getUserName} from '@dms/shared';

import {
  buildArray,
  composePath,
  isNotNilOrEmpty as isNotNilOrEmptyShared,
  Nullish,
  parseDate,
  useNavigate,
} from 'shared/web';

import {useInterestData} from './useInterestData';

export function useInterestParameters(
  interestId: string,
  hasComments: boolean,
  note: string | Nullish
) {
  const navigate = useNavigate();
  const {interest} = useInterestData(interestId);

  const formatDateTime = useDateTimeFormatter();

  const {timeToFirstResponse, hasEnded} = getTimeToFirstResponse(
    interest,
    interest?.firstResponseAt,
    interest?.ttfrBusinessMinutes
  );

  const {data: updatedByUserData} = useGetUserQuery(
    // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
    {id: interest?.updatedBy!},
    {skip: isNilOrEmpty(interest?.updatedBy)}
  );

  const onIconClick = () =>
    navigate(composePath(interestRoutes.detailOverview, {params: {id: interestId}}));

  return buildArray<Parameter>()
    .when(isNotNilOrEmptyShared(note), {
      icon: 'action/description',
      title: null,
      variant: 'secondary',
      tooltip: note,
      onClick: onIconClick,
    })
    .when(hasComments, {
      icon: 'communication/comment',
      title: null,
      variant: 'secondary',
      hideSeparator: true,
      onClick: onIconClick,
    })
    .when(isNotNil(interest?.publicId), interest?.publicId)
    .add(
      `${i18n.t('general.labels.created')}: ${
        isNotNilOrEmpty(interest?.createdAt)
          ? formatDateTime('dateTimeShort', parseDate(interest!.createdAt!))
          : EMPTY_PLACEHOLDER
      }`
    )
    .when(
      isNotNilOrEmpty(interest?.updatedAt),
      `${i18n.t('general.labels.changed')}: ${interest?.updatedAt ? formatDateTime('dateTimeShort', parseDate(interest.updatedAt)) : null}`
    )
    .when(
      isNotNilOrEmpty(updatedByUserData) && isNotNilOrEmpty(interest?.updatedBy),
      `${i18n.t('general.labels.changedBy')}: ${getUserName(updatedByUserData)}`
    )
    .when(isNotNilOrEmpty(timeToFirstResponse), {
      title: timeToFirstResponse,
      icon: 'devices/access_time',
      variant: hasEnded ? 'success' : 'danger',
      tooltip: i18n.t('general.label.timeToFirstResponse'),
    });
}
